import React from 'react';
import { motion } from 'framer-motion';
import WhatsAppConnect from '../components/whatsappConnect';

const PageNavbar = () => (
  <nav className="fixed top-0 w-full bg-white shadow-md z-50 flex items-center justify-between px-6 h-24 overflow-hidden">
    <img src="/Hydrocawach_logo.png" alt="HydroCawach Logo" className="h-36 w-auto object-contain" />
    <h1 className="absolute left-1/2 transform -translate-x-1/2 text-blue-600 text-lg md:text-2xl font-bold text-center">
      Chemical Process Intensification
    </h1>
  </nav>
);

// --- NEW DATA ARRAY FOR YOUR 4 POINTS ---
const synthesisPoints = [
  {
    title: 'Nanoparticle Synthesis',
    description: 'The implosion of bubbles generates localized “hot spots” and shock waves that accelerate nucleation and limit particle growth, leading to uniform particle size distribution. The high shear forces prevent agglomeration and support environmentally benign fabrication of nanomaterials.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.31h5.414a.563.563 0 01.321.988l-4.386 3.179a.563.563 0 00-.182.658l1.681 4.309a.563.563 0 01-.814.63l-4.386-3.178a.563.563 0 00-.658 0l-4.386 3.178a.563.563 0 01-.814-.63l1.681-4.309a.563.563 0 00-.182-.658L2.516 9.908a.563.563 0 01.321-.988h5.414a.563.563 0 00.475-.31l2.125-5.111z" />
      </svg>
    )
  },
  {
    title: 'Polymerization Reactions',
    description: 'Intense local energy release generates free radicals, enhancing polymerization rates. Cavitation also improves mixing of immiscible phases, leading to better control over polymer structure, molecular weight distribution, and energy-efficient polymer production.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    )
  },
  {
    title: 'Organic Synthesis',
    description: 'Enhances reaction kinetics and selectivity for transformations like oxidation, reduction, and esterification. The micro-mixing and localized heating facilitate rapid reactant dispersion, yielding higher conversions compared to conventional thermal methods.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.06 15.17l.875 1.518a2.237 2.237 0 002.12.9l4.5-4.5a2.237 2.237 0 00-.9-3.798l-4.5-4.5a2.237 2.237 0 00-3.798.9l-1.518.874m1.118 4.212a2.237 2.237 0 01-2.12.9l-4.5 4.5a2.237 2.237 0 01-3.798-.9l-.875-1.518a2.237 2.237 0 01.9-3.798l4.5-4.5a2.237 2.237 0 013.798.9l.875 1.518z" />
      </svg>
    )
  },
  {
    title: 'Green & Sustainable Synthesis',
    description: 'Aligns with green chemistry principles by enabling reactions under milder conditions, often eliminating toxic solvents or external heating. Reduced chemical consumption results in lower waste generation and supports sustainable manufacturing.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a.75.75 0 01-1.5 0v-5.25m0 0a3.75 3.75 0 01-3.75-3.75V8.25m3.75 0a3.75 3.75 0 013.75 3.75M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a.75.75 0 01-1.5 0v-5.25m0 0a3.75 3.75 0 01-3.75-3.75V8.25m3.75 0a3.75 3.75 0 013.75 3.75M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9-4.03-9-9-9z" />
      </svg>
    )
  }
];

const ChemicalProcessIntensification = () => (
  <div className="bg-white text-gray-800 min-h-screen relative overflow-x-hidden">
    <PageNavbar />

    <div className="pt-32 px-4"> {/* Increased top padding */}
      <div className="p-8 max-w-6xl mx-auto">

        {/* Introduction Paragraph */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-lg text-center text-gray-700 max-w-4xl mx-auto mb-16"
        >
          Cavitation has emerged as a versatile and energy-efficient tool for intensifying various
          chemical synthesis processes. The phenomenon involves the formation, growth,
          and collapse of vapor bubbles in a liquid medium, generating localized high
          temperatures, high pressures, and intense micro-turbulence, leading to enhanced
          molecular interactions and improved mass transfer.
        </motion.p>

        {/* New 2x2 Grid for the 4 Points */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {synthesisPoints.map((point, index) => (
            <motion.div
              key={point.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg shadow-lg p-6 flex flex-col hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full text-purple-600">
                  {point.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{point.title}</h3>
                  <p className="text-gray-700">{point.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </div>

    <WhatsAppConnect />
  </div>
);

export default ChemicalProcessIntensification;